import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  HostListener,
  input,
  InputSignal,
  OnInit,
  Signal,
  signal,
  viewChild,
} from '@angular/core';
import {
  DataForm,
  FlowAction,
  FlowConfig,
  FlowConnection,
  FlowNode,
  FlowNodePort,
  FlowNodeTypes,
  FlowState,
  FlowSticker,
  FlowTrigger,
  GraphModes,
  NodeEditDialogOutput,
  Position,
  SideNavModes,
  START_NODE_ID,
  WorkflowActionGroups,
  WorkflowActionGroupsMap,
  WorkflowConnectionExport,
  WorkflowExport,
} from './flow-chart.model';
import { MatDrawer, MatSidenavModule } from '@angular/material/sidenav';
import {
  EFMarkerType,
  FCanvasComponent,
  FConnectionContent,
  FCreateConnectionEvent,
  FFlowComponent,
  FFlowModule,
  FSelectionChangeEvent,
  FZoomDirective,
} from '@foblex/flow';
import { generateGuid } from '@foblex/utils';
import { cloneDeep } from 'lodash';
import { FlowNodeService } from '../../services/flow-node.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { SharedActions } from '@states/shared/shared.action-types';
import { MatMenuModule } from '@angular/material/menu';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { NodeEditModalComponent } from './components/node-edit-modal/node-edit-modal.component';
import { LOG_CONTEXT } from 'src/app/tokens/log-context.token';
import { ContextualLoggerService } from 'src/app/services/contextual-logger.service';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  concatMap,
  from,
  Observable,
  of,
  switchMap,
  take,
} from 'rxjs';
import { WorkflowActions } from '@states/workflow/workflow.action.types';
import { WorkflowModel } from '@models/workflow.model';
import { WorkflowsSelectors } from '@states/workflow/workflow.selector-types';
import { OrganizationSelectors } from '@states/organization/organization.selector-types';
import { ActiveOrganization } from '@models/organization.model';
import { TruncateDirective, UiBreadCrumbItem, UiButtonComponent, UiHeaderWithBreadcrumbsComponent, UiPreloaderComponent, UiUserSettingsDateComponent } from '@ui';
import { MatTooltip } from '@angular/material/tooltip';
import { InputDialog } from './components/input-modal/input-dialog';
import { HttpRequestModel } from '@models/http-request.model';
import { AlertsV2Selectors } from '@states/alerts-v2/alerts-v2.selector-types';
import { WorkflowWebhookModel } from '@models/workflow-webhook.model';
import { WorkflowWebhooksSelectors } from '@states/workflow-webhooks/workflow-webhooks.selector-types';
import { Dictionary } from '@ngrx/entity/src';
import { CdkCopyToClipboard } from '@angular/cdk/clipboard';
import { routerSegments } from '@consts/routes';
import { Actions, ofType } from '@ngrx/effects';
import { MatAccordion, MatExpansionModule } from '@angular/material/expansion';
import { UiSearchComponent } from '../ui-kit/ui-search/ui-search.component';
import { EditWorkflowDialogComponent, EditWorkflowDialogData } from './components/edit-workflow-dialog/edit-workflow-dialog.component';

@Component({
  selector: 'flow-chart',
  imports: [
    MatSidenavModule,
    FFlowModule,
    FZoomDirective,
    FConnectionContent,
    MatMenuModule,
    TranslatePipe,
    ReactiveFormsModule,
    UiButtonComponent,
    MatTooltip,
    TruncateDirective,
    CdkCopyToClipboard,
    UiUserSettingsDateComponent,
    MatAccordion,
    MatExpansionModule,
    UiSearchComponent,
    UiHeaderWithBreadcrumbsComponent,
    UiPreloaderComponent,
  ],
  templateUrl: './flow-chart.component.html',
  styleUrl: './flow-chart.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: LOG_CONTEXT, useValue: 'FlowChartComponent' },
    ContextualLoggerService,
  ],
})
export class FlowChartComponent implements OnInit {
  public config: InputSignal<FlowConfig> = input<FlowConfig>();

  private readonly _canvas = viewChild.required(FCanvasComponent);
  private readonly _flow = viewChild.required(FFlowComponent);
  private readonly _fZoom = viewChild.required(FZoomDirective);
  private readonly _drawer = viewChild(MatDrawer);

  protected readonly clipboard = signal<FlowState | undefined>(undefined);
  protected readonly hasSelectedItems = signal<boolean>(false);
  protected readonly sideNavModes = SideNavModes;
  protected readonly eMarkerType = EFMarkerType;
  protected readonly flowNodeTypes = FlowNodeTypes;
  protected readonly graphModes = GraphModes;
  protected readonly workflowActionGroupsMap = WorkflowActionGroupsMap;
  protected readonly workflowActionGroups = Object.keys(WorkflowActionGroupsMap);

  public selectGraph$: Observable<WorkflowModel.GetGraphResponse> =
    this.store.select(WorkflowsSelectors.selectGraph);
  public selectCreatedNode$: Observable<WorkflowModel.NodeConfigDocument> =
    this.store.select(WorkflowsSelectors.selectCreatedNode);

  private readonly _historySig = signal<
    { nodes: FlowNode[]; connections: FlowConnection[], stickers: FlowSticker[] }[]
  >([]);
  private readonly _futureSig = signal<
    { nodes: FlowNode[]; connections: FlowConnection[], stickers: FlowSticker[] }[]
  >([]);

  public nodesSig = signal<
    { nodes: FlowNode[], stickers: FlowSticker[] }>({ nodes: [], stickers: [] });
  public connectionsSig = signal<FlowConnection[]>([]);
  public selectedNodeSig = signal<
    | (FlowNode & { selectedOutputPort?: FlowNodePort; outputIndex?: number })
    | null
  >(null);
  public selectedStickerSig = signal<string | null>(null);
  public sideNavModeSig = signal<SideNavModes>(SideNavModes.TriggerMenu);
  public graphModeSig = signal<GraphModes>(GraphModes.Preview);
  public searchNodesSig = signal<string>('');

  public stickersSig: Signal<FlowSticker[]> = computed(() => {
    const nodes = this.nodesSig();
    return nodes.stickers;
  });
  public stepNodesSig: Signal<FlowNode[]> = computed(() => {
    const nodes = this.nodesSig();
    return nodes.nodes;
  });
  public invalidNodesSig: Signal<FlowNode[]> = computed(() => {
    const nodes = this.nodesSig().nodes || [];
    return nodes.filter(
      (node) => !this.getNodeIdFromGraph(node.id),
    );
  });

  public configActionsMapByGroupSig: Signal<Record<WorkflowActionGroups, FlowAction[]>> =
    computed(() => {
      const actions = this.config().actions;

      return actions.reduce((acc, action) => {
        if (acc[action.actionGroup]) {
          acc[action.actionGroup] = [...acc[action.actionGroup], action]
        } else {
          acc[action.actionGroup] = [action];
        }

        return acc
      }, {} as Record<WorkflowActionGroups, FlowAction[]>)
    });

  public filteredActionsSig: Signal<FlowAction[]> = computed(() => {
    const actions = this.config().actions;
    const search = this.searchNodesSig()?.toLowerCase();

    return actions.filter((action) => {
      return action.name.toLocaleLowerCase().includes(search)
        || action.actionGroup.toLocaleLowerCase().includes(search)
        || action.description?.toLocaleLowerCase().includes(search)
    })
  })

  public breadcrumbsSig: Signal<UiBreadCrumbItem[]> = computed(() => {
    return [
      {
        name: this.translateService.instant('BREADCRUMBS.WORKFLOWS'),
        route: `/${routerSegments.workflows}`,
      },
      {
        name: this.selectGraphSig()?.preview.name || '',
      },
    ];
  })

  public startNode = {
    position: { x: 0, y: 0 },
    id: START_NODE_ID,
  };

  public dataForm: FormGroup<DataForm> = this.fb.group({
    name: [this.translateService.instant('FLOW_CHART.DEFAULT_NAME')],
    tags: [null],
  });

  public selectOrganizationSig: Signal<ActiveOrganization> =
    this.store.selectSignal(OrganizationSelectors.selectActiveOrganization);
  public selectGraphSig: Signal<WorkflowModel.GetGraphResponse> =
    this.store.selectSignal(WorkflowsSelectors.selectGraph);
  public selectIsLoadingSig: Signal<boolean> =
    this.store.selectSignal(WorkflowsSelectors.selectIsLoading);
  public selectHttpRequestsSig: Signal<HttpRequestModel.DocumentMongo[]> =
    this.store.selectSignal(AlertsV2Selectors.selectHttpRequests);
  public selectWebhooksEntitiesSig: Signal<Dictionary<WorkflowWebhookModel.WebhookEndpointMongoDocument>> =
    this.store.selectSignal(
      WorkflowWebhooksSelectors.selectWorkflowWebhooksEntities,
    );

  constructor(
    private flowNodeService: FlowNodeService,
    private translateService: TranslateService,
    private store: Store,
    private fb: FormBuilder,
    private dialog: MatDialog,
    private logger: ContextualLoggerService,
    private route: ActivatedRoute,
    private router: Router,
    private destroyRef: DestroyRef,
    private actions$: Actions,
  ) { }

  public ngOnInit(): void {
    this.route.params
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap((params) => {
          if (params['id']) {
            const graphId = params['id'] as string;
            const orgId = this.selectOrganizationSig().orgId as string;

            this.store.dispatch(
              WorkflowActions.getGraph({ id: graphId, orgId: orgId }),
            );

            this.selectGraph$
              .pipe(takeUntilDestroyed(this.destroyRef))
              .subscribe((graph: WorkflowModel.GetGraphResponse) => {
                if (graph) {
                  console.log(graph);
                  this.dataForm.controls.name.setValue(graph.preview.name, {
                    emitEvent: false,
                  });

                  if (graph.preview.uiData?.nodes?.length) {
                    this.nodesSig.set({
                      nodes: [...graph.preview.uiData.nodes],
                      stickers: [...graph.preview.uiData.stickers ?? []],
                    });

                    this.connectionsSig.set(graph.preview.uiData.connections);
                  }
                }
              });
          }

          return of(params);
        }),
      )
      .subscribe();
  }

  public openEditWorkflowDialog() {
    const dialogData: EditWorkflowDialogData = {
      ...this.dataForm.getRawValue(),
      graphMode: this.graphModeSig(),
    };

    this.dialog
      .open(EditWorkflowDialogComponent, {
        panelClass: 'modal-no-padding',
        height: '262px',
        width: '248px',
        data: dialogData,
      })
      .afterClosed()
      .subscribe((newFormData?: { name: string, tags?: string[] }) => {
        if (newFormData) {
          this.dataForm.patchValue(newFormData);
          this._updateGraph();
        }
      })
  }

  public changeGraphMode(mode: GraphModes): void {
    void this._drawer().close();

    const graph = this.selectGraphSig();

    switch (mode) {
      case GraphModes.Preview:
        if (graph.preview.uiData?.nodes?.length) {
          this.nodesSig.set({
            nodes: [...graph.preview.uiData.nodes],
            stickers: [...graph.preview.uiData.stickers ?? []],
          });
          this.connectionsSig.set(graph.preview.uiData.connections);
        }

        this.graphModeSig.set(mode);
        break;
      case GraphModes.Publish:
        if (graph.published) {
          if (graph.published.uiData?.nodes?.length) {
            this.nodesSig.set({
              nodes: [...graph.published.uiData.nodes],
              stickers: [...graph.published.uiData.stickers ?? []],
            });
            this.connectionsSig.set(graph.published.uiData.connections);
          }

          this.graphModeSig.set(mode);
        }
        break;
      default:
        break;
    }
  }

  public addTriggerNode(trigger: FlowTrigger): void {
    const selectedNode = this.selectedNodeSig();

    //DISABLE FOR DEMO

    // const alreadyConnected = this.connectionsSig().some(
    //   (c) => c.from === selectedNode?.selectedOutputPort.id,
    // );

    // if (alreadyConnected) {
    //   this._showCreateConnectionLimitErrorMessage();
    //   return;
    // }

    this._pushHistory();

    const center = this.flowNodeService.getViewportCenter(this._canvas());
    const baseNode = this.flowNodeService.createTriggerNode(trigger, center);

    const node: FlowNode = {
      ...baseNode,
      position: selectedNode
        ? this.flowNodeService.calculateConnectedNodePosition(
          selectedNode.position,
          selectedNode.outputIndex,
          selectedNode.outputs.length,
        )
        : this.flowNodeService.findNonOverlappingPosition(
          baseNode.position,
          this.nodesSig().nodes,
        ),
    };

    this.nodesSig.set({
      ...this.nodesSig(),
      nodes: [...this.nodesSig().nodes, node],
    });

    this._updateGraph();
  }

  public addActionNode(action: FlowAction): void {
    const selectedNode = this.selectedNodeSig();

    if (!selectedNode) {
      return;
    }

    //DISABLE FOR DEMO

    // const alreadyConnected = this.connectionsSig().some(
    //   (c) => c.from === selectedNode?.selectedOutputPort.id,
    // );

    // if (alreadyConnected) {
    //   this._showCreateConnectionLimitErrorMessage();
    //   return;
    // }

    this._pushHistory();

    const position = this.flowNodeService.calculateConnectedNodePosition(
      selectedNode.position,
      selectedNode.outputIndex,
      selectedNode.outputs.length,
    );

    const node = this.flowNodeService.createActionNode(action, position);
    const connection = this._createConnectionFromSelectedNode(node);

    this._addNodeWithConnection(node, connection);

    this._updateGraph();
  }

  public addSticker(): void {
    const targetCenter = this.flowNodeService.getViewportCenter(this._canvas());

    const sticker: FlowSticker = {
      id: generateGuid(),
      nodeType: FlowNodeTypes.Sticker,
      position: targetCenter,
      text: this.translateService.instant('FLOW_CHART.EDIT_STICKER_TEXT'),
      color: '',
    };

    this.nodesSig.update((flowNodes) => {
      return {
        ...flowNodes,
        stickers: [...flowNodes.stickers, sticker],
      }
    });

    this._updateGraph();
  }

  public deleteSticker(id: string): void {
    this._pushHistory();

    this.nodesSig.update((state) => {
      return {
        ...state,
        stickers: state.stickers.filter((s) => s.id !== id),
      }
    });

    this._updateGraph();
  }

  public deleteNode(id: string): void {
    this._pushHistory();

    const graph = this.selectGraphSig();

    const nodeIdFromBE = graph.preview.nodes.find(
      (graphNode) => graphNode.uiId === id,
    )?.nodeId;

    if (nodeIdFromBE) {
      const orgId = this.selectOrganizationSig().orgId as string;

      this.store.dispatch(
        WorkflowActions.deleteNode({
          orgId: orgId,
          graphId: this.selectGraphSig()?.graphId,
          id: nodeIdFromBE,
        }),
      );

      this.actions$
        .pipe(
          ofType(WorkflowActions.deleteNodeSuccess),
          take(1),
        )
        .subscribe(() => {
          this._deleteNodeAndConnections(id);
          this._updateGraph();
        })
    } else {
      this._deleteNodeAndConnections(id);
      this._updateGraph();
    }
  }

  public deleteNode$(id: string) {
    const graph = this.selectGraphSig();

    const nodeIdFromBE = graph.preview.nodes.find(
      (graphNode) => graphNode.uiId === id,
    )?.nodeId;

    const orgId = this.selectOrganizationSig().orgId as string;

    this.store.dispatch(
      WorkflowActions.deleteNode({
        orgId,
        graphId: this.selectGraphSig()?.graphId,
        id: nodeIdFromBE,
      }),
    );

    return this.actions$.pipe(
      ofType(WorkflowActions.deleteNodeSuccess),
      take(1),
    );
  }

  public initNode$(node: FlowNode) {
    if (!node.data?.wasInited) {
      return of(null);
    }

    const dto = this.flowNodeService.buildNodeUpdateDto(node, node.data);

    this.store.dispatch(
      WorkflowActions.initNode({
        orgId: this.selectOrganizationSig().orgId,
        graphId: this.selectGraphSig()?.graphId,
        node: {
          ...dto,
          nodeType: node.type,
          uiNodeId: node.id,
        },
      }),
    );

    return this.actions$.pipe(
      ofType(WorkflowActions.initNodeSuccess),
      take(1)
    );
  }

  public deleteConnection(id: string): void {
    this._pushHistory();

    this.connectionsSig.update((connections) =>
      connections.filter((connection) => connection.id !== id),
    );

    this._updateGraph();
  }

  public startEditSticker(sticker: FlowSticker, event: MouseEvent): void {
    event.stopPropagation();
    this.selectedStickerSig.set(sticker.id);
  }

  public onStickerTextChange(id: string, event: Event): void {
    const value = (event.target as HTMLTextAreaElement).value;

    this.nodesSig.update((flowNodes) => {
      return {
        ...flowNodes,
        stickers: flowNodes.stickers.map((node) =>
          node.id === id
            ? { ...node, text: value }
            : node,
        ),
      }
    });
  }

  public finishEditSticker(): void {
    this.selectedStickerSig.set(null);
  }

  public openActionMenu(
    node: FlowNode,
    outputPort: FlowNodePort,
    outputIndex: number,
  ): void {
    this.sideNavModeSig.set(SideNavModes.ActionMenu);

    this.selectedNodeSig.set({
      ...node,
      selectedOutputPort: outputPort,
      outputIndex: outputIndex,
    });
  }

  public openNodeEditModal(
    node: FlowNode,
    shouldMarkFormAsTouched?: boolean,
  ): void {
    const prevNode = this.flowNodeService.getPreviousNodes(
      node,
      this.nodesSig().nodes,
      this.connectionsSig(),
    );

    const dialogData = {
      id: node.id,
      type: node.type,
      nodeType: node.nodeType,
      name: node.name,
      data: {
        previousNodes: prevNode,
        ...(node.data ?? {}),
        ...(shouldMarkFormAsTouched ? { shouldMarkFormAsTouched: true } : {}),
        disableForm: this.graphModeSig() === GraphModes.Publish,
      },
    };

    const orgId = this.selectOrganizationSig().orgId as string;
    const nodeIdFromBE = this.getNodeIdFromGraph(node.id);

    this.store.dispatch(WorkflowActions.resetNode());

    if (nodeIdFromBE) {
      this.store.dispatch(WorkflowActions.getNode({
        orgId: orgId,
        graphId: this.selectGraphSig()?.graphId,
        id: nodeIdFromBE,
      }))
    }

    this.dialog
      .open(NodeEditModalComponent, {
        panelClass: 'modal-no-padding',
        maxHeight: '80vh',
        minWidth: '426px',
        data: dialogData,
      })
      .afterClosed()
      .subscribe((dialogData) => {
        if (dialogData) {
          this._syncNodeWithBE(node, dialogData.data);

          this.actions$
            .pipe(
              ofType(
                WorkflowActions.editNodeSuccess,
                WorkflowActions.initNodeSuccess
              ),
              take(1)
            )
            .subscribe(() => {
              const newNodeData = this.flowNodeService.buildNodeData({
                node: node,
                dialogData: dialogData,
                webhooks: this.selectWebhooksEntitiesSig(),
                httpRequests: this.selectHttpRequestsSig(),
              });

              this.nodesSig.update((state) => {
                return {
                  ...state,
                  nodes: state.nodes.map((node) => {
                    return node.id === (dialogData as NodeEditDialogOutput).id
                      ? {
                        ...node,
                        data: {
                          ...newNodeData,
                          wasInited: true,
                        },
                      }
                      : node;
                  }),
                }
              });

              this._updateGraph();
            });
        }
      });
  }

  public openRunList() {
    void this.router.navigate([routerSegments.workflows, this.selectGraphSig()?.graphId, routerSegments.runs]);
  }

  public onZoomIn(): void {
    this._fZoom().zoomIn();
  }

  public onZoomOut(): void {
    this._fZoom().zoomOut();
  }

  public onFitToScreen(): void {
    this._canvas().resetScaleAndCenter();
  }

  public downloadJson(): void {
    const formData = this.dataForm.getRawValue();

    const workflowJson = this.flowNodeService.toWorkflowJson(
      this.nodesSig(),
      this.connectionsSig(),
      formData.name,
    );

    const json = JSON.stringify(workflowJson, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = `${formData.name || 'workflow'}.json`;
    link.click();

    URL.revokeObjectURL(url);
  }

  public onImportFile(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) return;

    const reader = new FileReader();

    reader.onload = () => {
      try {
        const parsed: unknown = JSON.parse(reader.result as string);
        this._applyImportedData(parsed as WorkflowExport);
      } catch (err) {
        this.logger.error(err);
        this._showImportFailMessage();
      }
    };

    reader.readAsText(file);
    input.value = '';
  }

  public publish(): void {
    this.updateConnections();
  }

  public run(nodeUiId: string): void {
    const graph = this.selectGraphSig();

    const orgId = this.selectOrganizationSig().orgId as string;
    const firstNodeId = graph.preview.nodes.find(
      (node) => node.uiId === nodeUiId,
    )?.nodeId;

    this.dialog
      .open(InputDialog, {
        panelClass: 'modal-no-padding',
        maxHeight: '80vh',
        width: '1048px',
      })
      .afterClosed()
      .subscribe((dialogData) => {
        const runDto = { startNodeId: firstNodeId, input: { message: dialogData } };

        this.store.dispatch(
          WorkflowActions.runGraphWorkflow({
            orgId: orgId,
            graphId: this.selectGraphSig()?.graphId,
            runGraph: runDto,
          }),
        );

      })
  }

  public getNodeIdFromGraph(id: string): string | undefined {
    const graph = this.selectGraphSig()

    const nodeIdFromGraph: string | undefined = graph.preview.nodes.find(
      (graphNode) => graphNode.uiId === id,
    )?.nodeId;

    return nodeIdFromGraph;
  }

  public showCopiedNodesToast() {
    this.store.dispatch(
      SharedActions.showMessage({
        success: this.translateService.instant('FLOW_CHART.COPY_TOAST'),
      }),
    );
  }

  public showCopiedToast() {
    this.store.dispatch(
      SharedActions.showMessage({
        success: this.translateService.instant('COMMON.COPY_CLIPBOARD'),
      }),
    );
  }

  private updateConnections(): void {
    const nodes = this.nodesSig();
    const connections = this.connectionsSig();

    const flowNodes = nodes.nodes;

    const graph = this.selectGraphSig();

    const workflowConnections: WorkflowConnectionExport[] =
      this.flowNodeService.buildBEConnectionsAndAssignNodeIds(
        connections,
        flowNodes,
        graph,
      );

    const updateConnectionsDto: WorkflowModel.UpdateConnectionsDto = {
      connections: workflowConnections,
    };

    const orgId = this.selectOrganizationSig().orgId as string;

    this.store.dispatch(
      WorkflowActions.updateConnections({
        orgId: orgId,
        graphId: this.selectGraphSig()?.graphId,
        updateConnections: updateConnectionsDto,
      }),
    );
  }

  private cut(): void {
    const selection = this._flow().getSelection();
    const nodeIds = selection.fNodeIds ?? [];
    const connectionIds = selection.fConnectionIds ?? [];

    if (!nodeIds.length && !connectionIds.length) return;

    this._pushHistory();

    this._copyInternal(nodeIds, connectionIds);

    const removeNodeSet = new Set(nodeIds);

    const cutNodes = this.nodesSig().nodes.filter((n) => removeNodeSet.has(n.id));
    const cutInitedNodes = cutNodes.filter((node) => node.data?.wasInited);
    const cutStickers = this.nodesSig().stickers.filter((s) => removeNodeSet.has(s.id));

    for (const node of cutNodes) {
      this._deleteNodeAndConnections(node.id);
    }

    this._updateGraph();

    from(cutInitedNodes)
      .pipe(
        concatMap((node) => this.deleteNode$(node.id))
      )
      .subscribe();

    for (const sticker of cutStickers) {
      this.deleteSticker(sticker.id);
    }

    this.hasSelectedItems.set(false);
  }

  private copy(): void {
    const selection = this._flow().getSelection();

    const nodeIds = selection.fNodeIds ?? [];
    const connectionIds = selection.fConnectionIds ?? [];

    if (!nodeIds.length && !connectionIds.length) return;

    this._copyInternal(nodeIds, connectionIds);

    this.showCopiedNodesToast();
  }

  private paste(): void {
    const clip = this.clipboard();
    if (!clip?.flowNodes.nodes?.length && !clip?.flowNodes.stickers?.length) return;

    this._pushHistory();

    const targetCenter = this.flowNodeService.getViewportCenter(this._canvas());

    const { nodes: newNodes, connections: newConnections, stickers: newStickers } =
      this.flowNodeService.remapClipboardToViewport(clip, targetCenter);

    this.nodesSig.update((state) => {
      return {
        nodes: [...state.nodes, ...newNodes],
        stickers: [...state.stickers, ...newStickers],
      };
    });

    this.connectionsSig.update((connections) => {
      return [...(connections ?? []), ...newConnections];
    });

    this._updateGraph();

    this._flow().select(
      newNodes.map((n) => n.id),
      newConnections.map((c) => c.id),
      false,
    );

    this.hasSelectedItems.set(true);

    from(newNodes)
      .pipe(
        concatMap((node) => this.initNode$(node))
      )
      .subscribe();
  }

  private undo(): void {
    if (!this._canUndo()) return;
    const history = [...this._historySig()];
    const current = this._createSnapshot();
    const previous = history.pop()!;

    const initedNodes: FlowNode[] = [];
    const deletedNodes: FlowNode[] = [];

    if (current?.nodes?.length > previous.nodes?.length) {
      current.nodes.forEach((node) => {
        const isNodeExist = previous.nodes.find((previousNode) => previousNode.id === node.id);

        if (!isNodeExist) {
          const nodeIdFromBE = this.getNodeIdFromGraph(node.id);

          if (nodeIdFromBE) {
            deletedNodes.push(node);
          }
        }
      })
    }

    if (current?.nodes?.length < previous.nodes?.length) {
      previous.nodes.forEach((node) => {
        const isNodeExist = current.nodes.find((currentNode) => currentNode.id === node.id);

        if (!isNodeExist) {
          initedNodes.push(node);
        }
      })
    }

    this._historySig.set(history);
    this._futureSig.update((future) => [...future, current]);

    this._applySnapshot(previous);
    this._updateGraph();

    from(initedNodes)
      .pipe(
        concatMap((node) => this.initNode$(node))
      )
      .subscribe();

    from(deletedNodes)
      .pipe(
        concatMap((node) => this.deleteNode$(node.id))
      )
      .subscribe();
  }

  private redo(): void {
    if (!this._canRedo()) return;

    const future = [...this._futureSig()];
    const current = this._createSnapshot();
    const next = future.pop()!;

    const initedNodes: FlowNode[] = [];
    const deletedNodes: FlowNode[] = [];

    if (current?.nodes?.length < next.nodes?.length) {
      next.nodes.forEach((node) => {
        const isNodeExist = current.nodes.find((currentNode) => currentNode.id === node.id);

        if (!isNodeExist) {
          initedNodes.push(node);
        }
      })
    }

    if (current?.nodes?.length > next.nodes?.length) {
      current.nodes.forEach((node) => {
        const isNodeExist = next.nodes.find((nextNode) => nextNode.id === node.id);

        if (!isNodeExist) {
          const nodeIdFromBE = this.getNodeIdFromGraph(node.id);

          if (nodeIdFromBE) {
            deletedNodes.push(node);
          }
        }
      })
    }

    this._futureSig.set(future);
    this._historySig.update((history) => [...history, current]);
    this._applySnapshot(next);
    this._updateGraph();

    from(initedNodes)
      .pipe(
        concatMap((node) => this.initNode$(node))
      )
      .subscribe();

    from(deletedNodes)
      .pipe(
        concatMap((node) => this.deleteNode$(node.id))
      )
      .subscribe();
  }

  @HostListener('window:keydown', ['$event'])
  public handleKeydown(event: KeyboardEvent): void {
    const target = event.target as HTMLElement;

    // Ignore typing inside inputs / textareas / contenteditable
    if (
      target.tagName === 'INPUT' ||
      target.tagName === 'TEXTAREA' ||
      target.isContentEditable
    ) {
      return;
    }

    const isMac = navigator.platform.toUpperCase().includes('MAC');
    const modifierPressed = isMac ? event.metaKey : event.ctrlKey;

    if (!modifierPressed) return;

    switch (event.key.toLowerCase()) {
      case 'c':
        if (!this.hasSelectedItems()) return;
        event.preventDefault();
        this.copy();
        break;

      case 'x':
        if (!this.hasSelectedItems()) return;
        event.preventDefault();
        this.cut();
        break;

      case 'v':
        if (!this.clipboard()) return;
        event.preventDefault();
        this.paste();
        break;

      case 'z':
        event.preventDefault();
        if (!isMac && event.key.toLowerCase() === 'y') {
          // Ctrl + Y -> Redo
          this.redo();
        } else if (isMac && event.shiftKey) {
          // Cmd + Shift + Z -> Redo
          this.redo();
        } else {
          // Cmd/Ctrl + Z -> Undo
          this.undo();
        }
        break;
    }
  }

  //Canvas events

  public loaded(): void {
    this._canvas()?.resetScaleAndCenter(false);
  }

  public selectionChanged(event: FSelectionChangeEvent): void {
    this.hasSelectedItems.set(
      (event.fNodeIds?.length && !event.fNodeIds.includes(START_NODE_ID)
        ? event.fNodeIds?.length
        : 0) > 0,
    );
  }

  public nodePositionChange(position: Position, id: string, isSticker: boolean = false): void {
    this._pushHistory();

    if (isSticker) {
      this.nodesSig.update((state) => {
        return {
          ...state,
          stickers: state.stickers.map((node) => (node.id === id ? { ...node, position } : node)),
        }
      });
    } else {
      this.nodesSig.update((state) => {
        return {
          ...state,
          nodes: state.nodes.map((node) => (node.id === id ? { ...node, position } : node)),
        }
      });
    }

    this._updateGraph();
  }

  public onCreateConnection(event: FCreateConnectionEvent): void {
    if (!event.fInputId) {
      return;
    }

    this._pushHistory();
    const connection = this.flowNodeService.createConnection(
      event.fOutputId,
      event.fInputId,
    );
    this.connectionsSig.set([...this.connectionsSig(), connection]);

    this._updateGraph();
  }

  // HTML click handlers
  public closeDrawerAndClearSelection(): void {
    void this._drawer().close();
    this.selectedNodeSig.set(null);
  }

  public closeDrawerAndResetMenu(): void {
    void this._drawer().close();
    this.selectedNodeSig.set(null);
    this.sideNavModeSig.set(SideNavModes.TriggerMenu);
  }

  public openDrawerAndActionMenu(
    node: FlowNode,
    outputPort: FlowNodePort,
    outputIndex: number,
    event: MouseEvent,
  ): void {
    event.stopPropagation();
    if (this.graphModeSig() === GraphModes.Preview) {
      void this._drawer().open();
      this.openActionMenu(node, outputPort, outputIndex);
    }
  }

  public deleteSelectedNode(): void {
    this.deleteNode(this.selectedNodeSig()?.id);
  }

  public openSeletedNodeEditModal(): void {
    this.openNodeEditModal(this.selectedNodeSig());
  }

  public copySeletedNode(): void {
    this._copyInternal([this.selectedNodeSig()?.id], []);
    this.showCopiedNodesToast();
  }

  public switchToTriggerMenu(): void {
    this.sideNavModeSig.set(SideNavModes.TriggerMenu);
  }

  // Private helper methods

  private _deleteNodeAndConnections(id: string) {
    this.connectionsSig.set(
      this.flowNodeService.removeConnectionsForNode(
        id,
        this.nodesSig().nodes,
        this.connectionsSig(),
      ),
    );

    this.nodesSig.update((state) => {
      return {
        ...state,
        nodes: state.nodes.filter((node) => node.id !== id),
      }
    });

    this._clearSelectionsIfDeleted(id);
    this.sideNavModeSig.set(SideNavModes.TriggerMenu);
  }

  private _syncNodeWithBE(
    node: FlowNode,
    nodeData: unknown,
    shouldCreate?: boolean
  ): void {
    const orgId = this.selectOrganizationSig().orgId as string;
    const nodeIdFromBE = this.getNodeIdFromGraph(node.id);

    const dto = this.flowNodeService.buildNodeUpdateDto(node, nodeData);

    if (!nodeIdFromBE || shouldCreate) {

      this.store.dispatch(
        WorkflowActions.initNode({
          orgId,
          graphId: this.selectGraphSig()?.graphId,
          node: {
            ...dto,
            nodeType: node.type,
            uiNodeId: node.id,
          },
        }),
      );
    } else if (nodeIdFromBE) {
      this.store.dispatch(
        WorkflowActions.editNode({
          orgId,
          graphId: this.selectGraphSig()?.graphId,
          id: nodeIdFromBE,
          updateNodeOptions: dto,
        }),
      );
    }
  }

  private _createConnectionFromSelectedNode(
    targetNode: FlowNode,
  ): FlowConnection {
    const selectedNode = this.selectedNodeSig()!;
    const selectedOutput =
      selectedNode.selectedOutputPort ?? selectedNode.outputs[0];
    const targetInput = targetNode.inputs[0];
    return this.flowNodeService.createConnection(
      selectedOutput.id,
      targetInput.id,
    );
  }

  private _addNodeWithConnection(
    node: FlowNode,
    connection: FlowConnection,
  ): void {
    this.nodesSig.update((state) => {
      return {
        ...state,
        nodes: [...state.nodes, node]
      }
    })

    this.connectionsSig.set([...this.connectionsSig(), connection]);

    this.selectedNodeSig.set({
      ...node,
      selectedOutputPort: node.outputs[0],
      outputIndex: 0,
    });
  }

  private _clearSelectionsIfDeleted(id: string): void {
    if (this.selectedNodeSig()?.id === id) {
      this.selectedNodeSig.set(null);
    }
    if (this.selectedStickerSig() === id) {
      this.selectedStickerSig.set(null);
    }
  }

  private _copyInternal(ids: string[], connectionIds: string[]): void {
    const nodes = this.nodesSig().nodes.filter((node) => ids.includes(node.id));
    const stickers = this.nodesSig().stickers.filter((sticker) => ids.includes(sticker.id));
    const connections = (this.connectionsSig() ?? []).filter((c) => connectionIds.includes(c.id));

    this.clipboard.set(cloneDeep({
      flowNodes: {
        nodes: nodes, stickers: stickers
      },
      connections: connections
    }));
  }

  private _createSnapshot(): {
    nodes: FlowNode[];
    connections: FlowConnection[];
    stickers: FlowSticker[];
  } {
    return {
      nodes: cloneDeep(this.stepNodesSig()),
      connections: cloneDeep(this.connectionsSig()),
      stickers: cloneDeep(this.stickersSig()),
    };
  }

  private _reinitPersistedNodes(
    nodes: FlowNode[],
  ): void {
    nodes.forEach(node => {
      if (node.data?.wasInited) {
        this._syncNodeWithBE(node, node.data);
      }
    });
  }

  private _applySnapshot(snapshot: {
    nodes: FlowNode[];
    connections: FlowConnection[];
    stickers: FlowSticker[];
  }): void {
    this.nodesSig.set({
      nodes: cloneDeep(snapshot.nodes),
      stickers: cloneDeep(snapshot.stickers),
    });
    this.connectionsSig.set(cloneDeep(snapshot.connections));
    this._clearAllSelections();
  }

  private _clearAllSelections(): void {
    this.selectedNodeSig.set(null);
    this.selectedStickerSig.set(null);
    this.hasSelectedItems.set(false);
    this.sideNavModeSig.set(SideNavModes.TriggerMenu);
  }

  private _pushHistory(): void {
    const snapshot = this._createSnapshot();
    this._historySig.update((history) => [...history, snapshot]);
    this._futureSig.set([]);
  }

  private _canUndo(): boolean {
    return this._historySig().length > 0;
  }

  private _canRedo(): boolean {
    return this._futureSig().length > 0;
  }

  private _applyImportedData(data: WorkflowExport): void {
    try {
      const { flowNodes, connections, name, tags } =
        this.flowNodeService.fromWorkflowJson(data);

      this._pushHistory();

      this.nodesSig.set(cloneDeep(flowNodes));

      this.connectionsSig.set(connections);

      this._reinitPersistedNodes(flowNodes.nodes);

      this.dataForm.patchValue({
        name:
          name ??
          this.translateService.instant('FLOW_CHART.DEFAULT_NAME'),
        tags: tags ?? [],
      });

      this._clearAllSelections();
      this._recenterCanvas();

      this._showImportSuccessMessage();
    } catch (e) {
      this._showImportFailMessage();
    }
  }

  private _recenterCanvas(): void {
    setTimeout(() => {
      this._canvas().resetScaleAndCenter();
    });
  }

  private _showImportSuccessMessage(): void {
    this.store.dispatch(
      SharedActions.showMessage({
        success: this.translateService.instant(
          'FLOW_CHART.IMPORTED_SUCCESSFULLY',
        ),
      }),
    );
  }

  private _showImportFailMessage(): void {
    this.store.dispatch(
      SharedActions.showMessage({
        error: this.translateService.instant('FLOW_CHART.INVALID_FILE'),
      }),
    );
  }

  // private _showCreateConnectionLimitErrorMessage(): void {
  //   this.store.dispatch(
  //     SharedActions.showMessage({
  //       error: this.translateService.instant('FLOW_CHART.MULTI_OUTPUT_ERROR'),
  //     }),
  //   );
  // }

  private _updateGraph(): void {
    if (this.graphModeSig() === GraphModes.Preview) {
      const nodes = this.nodesSig().nodes

      const stickers = this.nodesSig().stickers

      const graphMetadata: WorkflowModel.UpdateGraphMetadataDto = {
        name: this.dataForm.controls.name.value,
        uiData: {
          nodes: nodes ?? [],
          connections: this.connectionsSig(),
          stickers: stickers ?? [],
        },
      };

      const updateGraphData = {
        graphMetadata: graphMetadata,
        id: this.selectGraphSig()?.graphId,
        orgId: this.selectOrganizationSig().orgId,
      };

      this.store.dispatch(WorkflowActions.updateGraph(updateGraphData));
    }
  }
}
